import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Lightbox, Tag, cx } from '@raro/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { EVIDENCE_TYPE_LABELS, HUMAN_STATUS_LABELS, formatDate, locationLabel } from '../lib/format';
import type { Evidence } from '../lib/types';

export function EvidenceCard({ evidence, canValidate, showSource }: { evidence: Evidence; canValidate: boolean; showSource?: boolean }) {
  const qc = useQueryClient();
  const validate = useMutation({
    mutationFn: (status: 'accepted' | 'rejected') => api.patch(`/evidence/${evidence.id}/validate`, { status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['source'] });
      void qc.invalidateQueries({ queryKey: ['evidence'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/evidence/${evidence.id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['source'] }); void qc.invalidateQueries({ queryKey: ['evidence'] }); void qc.invalidateQueries({ queryKey: ['overview'] }); },
  });
  const loc = locationLabel(evidence.location);
  const [zoom, setZoom] = useState(false);
  return (
    <article className={cx('rr-evidence', evidence.image && 'rr-evidence--photo', evidence.humanStatus === 'accepted' && 'rr-evidence--accepted', evidence.humanStatus === 'rejected' && 'rr-evidence--rejected')}>
      {evidence.image && (
        <button type="button" className="rr-evidence__thumb" onClick={() => setZoom(true)} aria-label="ver a imagem maior">
          <img src={evidence.imageThumb ?? evidence.image} alt="" loading="lazy" />
          {loc && evidence.location?.startMs !== undefined && <span className="rr-evidence__time">{loc}</span>}
        </button>
      )}
      <div className="rr-evidence__body">
      <p className="rr-evidence__quote">“{evidence.content}”</p>
      {evidence.note && <p className="rr-evidence__note">{evidence.note}</p>}
      <div className="rr-evidence__meta">
        <Tag tone={evidence.humanStatus === 'accepted' ? 'accent' : 'outline'}>{HUMAN_STATUS_LABELS[evidence.humanStatus]}</Tag>
        <span>{EVIDENCE_TYPE_LABELS[evidence.evidenceType]}</span>
        <span>{evidence.generatedBy === 'human' ? 'marcada por pessoa' : evidence.generatedBy === 'ai' ? (evidence.humanStatus === 'accepted' && !evidence.validatedBy ? 'lida pela Machine' : 'sugerida pela Machine') : 'regra'}</span>
        {showSource && evidence.source && (
          <Link to={`/fontes/${evidence.sourceId}`} style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>
            {evidence.source.title}
          </Link>
        )}
        {loc && <span>{loc}</span>}
        <span>{formatDate(evidence.createdAt)}</span>
        {canValidate && evidence.generatedBy === 'ai' && evidence.humanStatus === 'accepted' && !evidence.validatedBy && (
          <button type="button" className="rr-budget__toggle" style={{ marginLeft: 'auto' }} onClick={() => remove.mutate()} disabled={remove.isPending} title="a Machine leu errado? apague.">apagar</button>
        )}
        {canValidate && evidence.humanStatus === 'under_review' && (
          <span className="rr-row" style={{ marginLeft: 'auto' }}>
            <Button size="sm" variant="primary" onClick={() => validate.mutate('accepted')} disabled={validate.isPending}>
              aceitar
            </Button>
            <Button size="sm" variant="quiet" onClick={() => validate.mutate('rejected')} disabled={validate.isPending}>
              rejeitar
            </Button>
          </span>
        )}
      </div>
      </div>
      {evidence.image && <Lightbox items={[{ src: evidence.image, caption: `${evidence.source?.title ?? ''}${loc ? ` · ${loc}` : ''}` }]} index={zoom ? 0 : null} onClose={() => setZoom(false)} />}
    </article>
  );
}
